import {
  DEFAULT_SCENE_OPTICS_CONFIG,
  SCENE_OPTICS_RANGES,
} from "@/lib/laser-physics";
import {
  SceneOpticsConfig,
  SceneOpticsSystemResult,
} from "@/types/laser-engraving";
import { useControls } from "leva";
import { useCallback, useMemo } from "react";

/**
 * 场景光学效果配置管理Hook
 *
 * 提供 Shadow 和 Caustic 系统共享的物理参数统一管理功能，包括：
 * - 统一的光源位置控制
 * - 统一的效果平面位置控制
 * - Leva控制面板集成
 * - 参数验证和重置功能
 *
 * @param panelName - 控制面板名称
 * @param initialConfig - 初始配置（可选）
 * @returns 场景光学效果配置和控制函数
 */
export function useSceneOptics(
  panelName: string = "Scene Optics",
  initialConfig?: Partial<SceneOpticsConfig>,
): SceneOpticsSystemResult {
  // 合并初始配置
  const effectiveConfig = useMemo(() => {
    return { ...DEFAULT_SCENE_OPTICS_CONFIG, ...initialConfig };
  }, [initialConfig]);

  // 🎯 Leva控制面板配置 - 场景级光学效果统一管理
  const [config, set] = useControls(panelName, () => ({
    enabled: {
      value: effectiveConfig.enabled,
    },
    "Light Source": {
      value: false,
      render: () => false, // 分组标题
    },
    lightX: {
      value: effectiveConfig.lightSource[0],
      min: SCENE_OPTICS_RANGES.lightSource.min,
      max: SCENE_OPTICS_RANGES.lightSource.max,
      step: SCENE_OPTICS_RANGES.lightSource.step,
    },
    lightY: {
      value: effectiveConfig.lightSource[1],
      min: SCENE_OPTICS_RANGES.lightSource.min,
      max: SCENE_OPTICS_RANGES.lightSource.max,
      step: SCENE_OPTICS_RANGES.lightSource.step,
    },
    lightZ: {
      value: effectiveConfig.lightSource[2],
      min: SCENE_OPTICS_RANGES.lightSource.min,
      max: SCENE_OPTICS_RANGES.lightSource.max,
      step: SCENE_OPTICS_RANGES.lightSource.step,
    },
    "Effect Plane": {
      value: false,
      render: () => false, // 分组标题
    },
    planeX: {
      value: effectiveConfig.effectPlane[0],
      min: SCENE_OPTICS_RANGES.effectPlane.min,
      max: SCENE_OPTICS_RANGES.effectPlane.max,
      step: SCENE_OPTICS_RANGES.effectPlane.step,
    },
    planeY: {
      value: effectiveConfig.effectPlane[1],
      min: SCENE_OPTICS_RANGES.effectPlane.min,
      max: SCENE_OPTICS_RANGES.effectPlane.max,
      step: SCENE_OPTICS_RANGES.effectPlane.step,
    },
    planeZ: {
      value: effectiveConfig.effectPlane[2],
      min: SCENE_OPTICS_RANGES.effectPlane.min,
      max: SCENE_OPTICS_RANGES.effectPlane.max,
      step: SCENE_OPTICS_RANGES.effectPlane.step,
    },
  }));

  // 重置为默认值
  const resetToDefaults = useCallback(() => {
    console.log("🎯 重置场景光学效果为默认配置");
    const defaults = DEFAULT_SCENE_OPTICS_CONFIG;
    set({
      enabled: defaults.enabled,
      lightX: defaults.lightSource[0],
      lightY: defaults.lightSource[1],
      lightZ: defaults.lightSource[2],
      planeX: defaults.effectPlane[0],
      planeY: defaults.effectPlane[1],
      planeZ: defaults.effectPlane[2],
    });
  }, [set]);

  return {
    config: {
      enabled: config.enabled,
      lightSource: [config.lightX, config.lightY, config.lightZ],
      effectPlane: [config.planeX, config.planeY, config.planeZ],
    } as SceneOpticsConfig,
    resetToDefaults,
  };
}

/**
 * 场景光学效果配置验证函数
 * 验证配置是否在有效范围内
 *
 * @param config - 场景光学效果配置
 * @returns 验证结果和错误信息
 */
export function validateSceneOpticsConfig(config: SceneOpticsConfig): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // 验证启用状态（布尔值不需要范围检查）
  if (typeof config.enabled !== "boolean") {
    errors.push("启用状态必须是布尔值");
  }

  // 验证光源位置
  if (!Array.isArray(config.lightSource) || config.lightSource.length !== 3) {
    errors.push("光源位置必须是长度为3的数组");
  } else {
    config.lightSource.forEach((val, index) => {
      const axis = ["X", "Y", "Z"][index];
      if (
        val < SCENE_OPTICS_RANGES.lightSource.min ||
        val > SCENE_OPTICS_RANGES.lightSource.max
      ) {
        errors.push(
          `光源位置${axis}轴必须在${SCENE_OPTICS_RANGES.lightSource.min}-${SCENE_OPTICS_RANGES.lightSource.max}之间`,
        );
      }
    });
  }

  // 验证效果平面位置
  if (!Array.isArray(config.effectPlane) || config.effectPlane.length !== 3) {
    errors.push("效果平面位置必须是长度为3的数组");
  } else {
    config.effectPlane.forEach((val, index) => {
      const axis = ["X", "Y", "Z"][index];
      if (
        val < SCENE_OPTICS_RANGES.effectPlane.min ||
        val > SCENE_OPTICS_RANGES.effectPlane.max
      ) {
        errors.push(
          `效果平面位置${axis}轴必须在${SCENE_OPTICS_RANGES.effectPlane.min}-${SCENE_OPTICS_RANGES.effectPlane.max}之间`,
        );
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// 类型导出，方便其他文件使用
export type { SceneOpticsConfig, SceneOpticsSystemResult };